import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import zoeOrdersService from '../../services/zoe-orders-service';
import PackageDetailsBasicInformationTab from '../package_details/PackageDetailBasicInformationTab';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value);
};

export default function OrderInfo({ zoeOrderId }) {
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!zoeOrderId) return;

    const fetchPurchaseOrder = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await zoeOrdersService.getPurchaseOrder(zoeOrderId);
        if (response.success) {
          setPurchaseOrder(response.data);
        } else {
          setError(response.message || 'No linked purchase order found');
        }
      } catch (err) {
        setError('Failed to load purchase order');
        console.error('OrderInfo fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPurchaseOrder();
  }, [zoeOrderId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error || !purchaseOrder) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          {error || 'No linked purchase order found'}
        </Alert>
      </Box>
    );
  }

  const overviewItems = [
    { title: 'PO ID', value: purchaseOrder.ID },
    { title: 'PO Number', value: purchaseOrder.PONumber },
    { title: 'PO Title', value: purchaseOrder.POTitle },
    { title: 'PO Type', value: purchaseOrder.POType },
    { title: 'Status', value: purchaseOrder.Status },
    { title: 'Date Created', value: formatDate(purchaseOrder.DateCreated) },
    { title: 'Last Updated', value: formatDate(purchaseOrder.LastUpdated) },
    { title: 'Required Date', value: formatDate(purchaseOrder.RequiredDate) },
    { title: 'Date Placed', value: formatDate(purchaseOrder.DatePlaced) },
    { title: 'Is Placed', value: purchaseOrder.IsPlaced ? 'Yes' : 'No' },
    { title: 'Store ID', value: purchaseOrder.StoreID },
    { title: 'Worksheet ID', value: purchaseOrder.WorksheetID },
    { title: 'Supplier ID', value: purchaseOrder.SupplierID },
    { title: 'To', value: purchaseOrder.To },
    { title: 'Ship To', value: purchaseOrder.ShipTo },
    { title: 'Ship Via', value: purchaseOrder.ShipVia },
    { title: 'FOB Point', value: purchaseOrder.FOBPoint },
    { title: 'Terms', value: purchaseOrder.Terms },
    { title: 'Freight', value: purchaseOrder.Freight },
    { title: 'Requisitioner', value: purchaseOrder.Requisitioner },
    { title: 'Confirming To', value: purchaseOrder.ConfirmingTo },
    { title: 'Remarks', value: purchaseOrder.Remarks },
    { title: 'Tax Rate', value: purchaseOrder.TaxRate },
    { title: 'Shipping', value: formatCurrency(purchaseOrder.Shipping) },
    {
      title: 'Est. Shipping',
      value: formatCurrency(purchaseOrder.EstShipping)
    },
    {
      title: 'Current Shipping',
      value: formatCurrency(purchaseOrder.CurrentShipping)
    },
    { title: 'Other Fees', value: formatCurrency(purchaseOrder.OtherFees) },
    {
      title: 'Est. Other Fees',
      value: formatCurrency(purchaseOrder.EstOtherFees)
    },
    {
      title: 'Current Other Fees',
      value: formatCurrency(purchaseOrder.CurrentOtherFees)
    },
    { title: 'Exchange Rate', value: purchaseOrder.ExchangeRate },
    { title: 'Currency ID', value: purchaseOrder.CurrencyID },
    {
      title: 'Cost Distribution Method',
      value: purchaseOrder.CostDistributionMethod
    },
    { title: 'Batch Number', value: purchaseOrder.BatchNumber },
    { title: 'Other Store ID', value: purchaseOrder.OtherStoreID },
    { title: 'Other PO ID', value: purchaseOrder.OtherPOID },
    { title: 'Parent PO ID', value: purchaseOrder.ParentPOId },
    { title: 'Root PO ID', value: purchaseOrder.RootPOId },
    { title: 'Origin PO ID', value: purchaseOrder.OriginPOId },
    { title: 'Master PO', value: purchaseOrder.MasterPO },
    { title: 'Inventory Location', value: purchaseOrder.InventoryLocation },
    { title: 'Discrepancy Status', value: purchaseOrder.DiscrepancyStatus },
    { title: 'DB Timestamp', value: purchaseOrder.DBTimeStamp }
  ];

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={1}>
        <ShoppingCartIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          PO Number: <strong>{purchaseOrder.PONumber}</strong>
        </Typography>
      </Box>
      <PackageDetailsBasicInformationTab
        id={zoeOrderId}
        overviewItems={overviewItems}
        collapsibleItems={[]}
      />
    </Box>
  );
}
